// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { AppointmentForm } from '~/components/module/client';
import { BaseStatus, BaseTable, BaseToolbar, Loading, SortComponent, UnderDevelopment } from '~/components/common';
// Others
import { IParamDetailClient, IQueryDetailClient } from '~/utils/interface/client';
import { IBaseResponse, IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_APPOINTMENT,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IHeaderCellType, ISortBy } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailClient } from '~/thunks/client/clientThunk';
import { DateFormatEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { formatDate } from '~/utils/helper/common';
import { selectIsRefreshDetailClient } from '~/thunks/client/clientSlice';
import { IAppointment } from '~/utils/interface/appointment';
// Styles, images, icons
import styles from './AppointmentClientDetailTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string) => void
): IHeaderCellType<IAppointment>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'title',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_title_table')}</Box>
          {/* <SortComponent name='title' sortType={getSortType('title')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) => (
        <Stack>
          <Typography className={cx('textTitleTable')}>{record?.title}</Typography>
          <Typography className={cx('textNoteTable')}>{record?.note}</Typography>
        </Stack>
      ),
      minWidth: 120,
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_type_table')}</Box>
          {/* <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) => {
        return <Typography className={cx('textTable')}>{record?.type}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'date',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_title_date')}</Box>
          {/* <SortComponent name='date' sortType={getSortType('date')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) => {
        return <Typography className={cx('textTable')}>{formatDate(record?.date, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'start time',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_title_time')}</Box>
          {/* <SortComponent name='start time' sortType={getSortType('start time')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) => {
        return (
          <Typography className={cx('textTable')}>{`${formatDate(
            record?.startTime,
            DateFormatEnum.HH_mm_A
          )} - ${formatDate(record?.endTime, DateFormatEnum.HH_mm_A)}`}</Typography>
        );
      },
    },
    {
      id: 'location',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_title_location')}</Box>
          {/* <SortComponent name='location' sortType={getSortType('location')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) => {
        return <Typography className={cx('textTable')}>{record?.location}</Typography>;
      },
      minWidth: 160,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('appointment_title_status')}</Box>
          {/* <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} /> */}
        </Stack>
      ),
      render: (record: IAppointment) =>
        record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_STRING,
    },
    {
      id: 'action',
      label: t('appointment_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IAppointment) => (
        <Button onClick={() => onView(record.id)} variant='text'>
          {t('table_action_view', { ns: 'common/base' })}
        </Button>
      ),
    },
  ];
};

const AppointmentClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshDetailClient = useSelector(selectIsRefreshDetailClient);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_APPOINTMENT);
  const [paramObject, setParamObject] = useState<IQueryDetailClient>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [appointmentList, setAppointmentList] = useState<IAppointment[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [appointmentIdSelected, setAppointmentIdSelected] = useState<string>(EMPTY_STRING);
  const [isShowFormAppointment, setIsShowFormAppointment] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.APPOINTMENTS,
      query: paramObject,
    };

    handleGetListAppointment(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshDetailClient) return;

    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.APPOINTMENTS,
      query: paramObject,
    };

    handleGetListAppointment(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshDetailClient]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleGetListAppointment = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IAppointment[]>>;

        if (!data) return;

        const { responses, pagination } = data;

        if (!responses) return;

        setAppointmentList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (id: string) => {
    setAppointmentIdSelected(id);
    handleShowAppointmentForm();
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, quoteSortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handleShowAppointmentForm = () => {
    setIsShowFormAppointment(true);
  };

  const handleCloseAppointmentForm = () => {
    setIsShowFormAppointment(false);
    setAppointmentIdSelected(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Stack id='appointmentClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('appointment_title')} description={t('appointment_description')}>
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowAppointmentForm}
              startIcon={<img src={icons.menuIconScheduleActive} alt={'common_alt_img'} />}
            >
              {t('appointment_schedule_appointment_button')}
            </Button>
          </Box>
        </BaseToolbar>
      </Stack>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={appointmentList || []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t, sortBy, handleSort, handleView)}
          onPageChange={handlePageChange}
          onRowClick={handleView}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowFormAppointment && (
        <AppointmentForm appointmentId={appointmentIdSelected} onClose={handleCloseAppointmentForm} />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Stack>
  );
};

export default AppointmentClientDetailTab;
